/** @jsx h */
import { Fragment, h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { clearCart } from '../objects/cart/ajax_cart';
import DraftOrderConfirmationModal from './DraftOrderModal';
import API_ENDPOINTS from '../config/api.js';
import { proxyFetch } from '../objects/proxyAuth.js';

function DraftOrderPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draftOrder, setDraftOrder] = useState(null);
  const [addToCartErrorMessage, setAddToCartErrorMessage] = useState(null);
  const [paymentTerms, setPaymentTerms] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [isSalesRep, setIsSalesRep] = useState(false);

  useEffect(() => {
    // Check if user is a sales rep
    const salesRepData = document.getElementById('sales-rep-data');
    if (salesRepData) {
      const isSalesRepValue = salesRepData.dataset.isSalesRep === 'true';
      setIsSalesRep(isSalesRepValue);
    }
    
    fetchDraftOrder();
  }, []);

  // Fetch payment terms after draft order loads
  useEffect(() => {
    // console.log('Draft order loaded:', draftOrder);
    // console.log('purchasingEntity:', draftOrder?.purchasingEntity);

    // Check for company ID in the correct path
    const companyId = draftOrder?.purchasingEntity?.company?.id;

    if (companyId) {
      // console.log('Fetching payment terms for company:', companyId);
      fetchPaymentTerms(companyId);
    } else {
      // console.log(
      //   'No company ID found. purchasingEntity structure:',
      //   draftOrder?.purchasingEntity,
      // );
    }
  }, [draftOrder]);

  const fetchDraftOrder = async (retryCount = 0) => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const draftOrderId = urlParams.get('draft_id');

    try {
      const response = await proxyFetch(API_ENDPOINTS.DRAFT_ORDER_BY_ID, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          draft_id: `gid://shopify/DraftOrder/${draftOrderId}`,
        }),
      });

      if (!response.ok) {
        // Retry logic for 404 (draft not ready yet)
        if (response.status === 404 && retryCount < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          return fetchDraftOrder(retryCount + 1);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setDraftOrder(result.data);
      setError(null);
    } catch (error) {
      if (retryCount < 2) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return fetchDraftOrder(retryCount + 1);
      }
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentTerms = async (companyId) => {
    try {
      const response = await proxyFetch(API_ENDPOINTS.COMPANY_METAFIELD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Company resolved server-side from the signed customer id.
        body: JSON.stringify({
          metafield_key: 'custom.payment_terms',
        }),
      });
      const result = await response.json();
      // console.log('Payment terms response:', result);
      if (result.data?.value) {
        const terms = result.data.value.toLowerCase();
        // console.log('Setting payment terms:', terms);
        setPaymentTerms(terms);
      }
    } catch (error) {
      // console.error('Error fetching payment terms:', error);
    }
  };

  const markDraftAsPaid = async () => {
    setMarkingPaid(true);
    setActionMessage(null);

    const urlParams = new URLSearchParams(window.location.search);
    const draftOrderId = urlParams.get('draft_id');

    try {
      const response = await proxyFetch(API_ENDPOINTS.MARK_DRAFT_PAID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_id: `gid://shopify/DraftOrder/${draftOrderId}`,
        }),
      });

      const result = await response.json();

      if (result.error) {
        setActionMessage({
          type: 'error',
          text: 'Failed to mark order as paid',
        });
      } else {
        setActionMessage({
          type: 'success',
          text: 'Order marked as paid successfully',
        });
        // Refresh draft order data
        await fetchDraftOrder();
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Failed to mark order as paid' });
    } finally {
      setMarkingPaid(false);
    }
  };

  const sendDraftInvoice = async () => {
    setSendingInvoice(true);
    setActionMessage(null);

    const urlParams = new URLSearchParams(window.location.search);
    const draftOrderId = urlParams.get('draft_id');

    try {
      const response = await proxyFetch(API_ENDPOINTS.SEND_DRAFT_INVOICE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: `gid://shopify/DraftOrder/${draftOrderId}`,
          email: draftOrder.email,
        }),
      });

      const result = await response.json();

      if (result.error) {
        setActionMessage({ type: 'error', text: 'Failed to send invoice' });
      } else {
        setActionMessage({
          type: 'success',
          text: 'Invoice sent successfully',
        });
      }
    } catch (error) {
      setActionMessage({ type: 'error', text: 'Failed to send invoice' });
    } finally {
      setSendingInvoice(false);
    }
  };

  async function addDraftToCart() {
    try {

      const itemsToAdd = {
        items: draftOrder.lineItems.edges.map((lineItem) => ({
          id: lineItem.node.variant.id.split('/').pop(),
          quantity: lineItem.node.quantity,
        })),
      };

      await clearCart();

      const response = await fetch(`/cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemsToAdd),
      });

      const data = await response.json();

      if (data.items.length > 0) {
        window.location.href = '/cart';
      } else {
        setAddToCartErrorMessage(
          'There was a problem adding all items to the cart. Please try again.',
        );
      }
    } catch (error) {
      // console.error('Error:', error);
      setAddToCartErrorMessage(
        'There was a problem adding items to the cart. Please try again.',
      );
    }
  }

  return (
    <div className="draft-order-page">

      {loading && <div className="loading">Loading...</div>}
      {error && <div className="error">There was an error loading the Draft Order</div>}

      {draftOrder && (
        <>
          <div className="draft-order-page--header">
            <h1 className="draft-order-page__title">
              Draft Order {draftOrder.name}
            </h1>
          </div>
      
          <div className="page-width">
            <div className="draft-order-page__columns">
              {/* Left Column - Order Details */}
              <div className="draft-order-page__column draft-order-page__column--1">


                <div className="draft-order-card-row">
                  <h3 className="draft-order-page__subtitle">Products</h3>
                  {draftOrder.lineItems.edges.map((lineItem) => (
                    <div
                      className="draft-order-page__product"
                      key={lineItem.node.id || lineItem.node.variant.id}
                    >
                      <img
                        className="draft-order-page__image"
                        src={lineItem.node.image.src}
                        alt={lineItem.node.name}
                      />
                      <p className="draft-order-page__name">{lineItem.node.name}</p>
                      {draftOrder.discountCodes.length > 0 && (
                        <p>
                          {draftOrder.discountCodes.map((code) => (
                            <span className="draft-order-page__discount">{code}</span>
                          ))}{' '}
                          (-
                          {Number(
                            lineItem.node.originalUnitPrice -
                              lineItem.node.discountedUnitPrice,
                          ).toFixed(2)}{' '}
                         )
                        </p>
                      )}
                      {lineItem.node.discountedUnitPrice && (
                        <p
                          className={`draft-order-page__price ${
                            lineItem.node.originalUnitPrice !==
                            lineItem.node.discountedUnitPrice
                              ? 'discounted'
                              : ''
                          }`}
                        >
                       $
                          {lineItem.node.discountedUnitPrice}
                        </p>
                      )}
                      <p>Quantity: {lineItem.node.quantity}</p>
                      <p className="draft-order-page__total">Product Total</p>
                      {draftOrder.discountCodes.length > 0 && (
                        <p
                          className={`draft-order-page__price ${
                            lineItem.node.discountedTotalSet ? 'discounted' : ''
                          }`}
                        >
                          $
                          {lineItem.node.originalTotalSet.shopMoney.amount}
                        </p>
                      )}
                      {lineItem.node.discountedTotalSet && (
                        <p>
                          $
                          {lineItem.node.discountedTotalSet.shopMoney.amount}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Products */}
              <div className="draft-order-page__column draft-order-page__column--2">
                <div className="draft-order-card-row">
                  <h3 className="draft-order-page__subtitle">Order Details</h3>
                  <p>Order Number: {draftOrder.name}</p>

                  {/* COB2B-798 #5: what Shopify applied to the draft — the rep's code
                      by name, and the customer's group discount — so the rep can see
                      the code took before sending or submitting. */}
                  {(draftOrder.platformDiscounts || [])
                    .filter((discount) => Number(discount.totalAmount?.amount) > 0)
                    .map((discount) => (
                      <p key={discount.code || discount.title}>
                        Discount ({discount.code || discount.title}): -$
                        {Number(discount.totalAmount.amount).toFixed(2)}
                      </p>
                    ))}

                  <p>
                    Total: $
                    {Number(draftOrder.totalPriceSet.shopMoney.amount).toFixed(2)}
                  </p>

                  {actionMessage && (
                    <div
                      className={`draft-order-page__message draft-order-page__message--${actionMessage.type}`}
                    >
                      {actionMessage.text}
                    </div>
                  )}
                {paymentTerms && (
                  <div className="draft-order-page__actions">
                    {(paymentTerms === 'on account' || paymentTerms === 'prepay') &&
                      draftOrder?.status !== 'COMPLETED' && (
                        <button
                          className="draft-order-page__button send-invoice"
                          onClick={sendDraftInvoice}
                          disabled={sendingInvoice}
                        >
                          {sendingInvoice ? 'Sending...' : 'Send Invoice'}
                        </button>
                      )}

                    {paymentTerms === 'on account' && (
                      <button
                        className="draft-order-page__button mark-paid"
                        onClick={markDraftAsPaid}
                        disabled={markingPaid || draftOrder?.status === 'COMPLETED'}
                      >
                        {markingPaid
                          ? 'Marking as Paid...'
                          : draftOrder?.status === 'COMPLETED'
                            ? 'Already Paid'
                            : 'Mark as Paid'}
                      </button>
                    )}
                    <a href="/pages/draft-orders" className="draft-back-to-drafts draft-order-page__button">Back to drafts</a>
                  </div>
                )}

                {/* Create Cart from Order button - Sales Rep only */}
                {isSalesRep && (
                  <div className="draft-order-page__actions">
                    <button
                      className="draft-order-page__button create-cart"
                      onClick={() => setIsModalOpen(true)}
                    >
                      Create Cart from Order
                    </button>
                  </div>
                )}

                {/* Modal for cart creation confirmation */}
                {isModalOpen && (
                  <DraftOrderConfirmationModal
                    onCancel={() => setIsModalOpen(false)}
                    onContinue={addDraftToCart}
                  />
                )}

                {/* Error message for cart operations */}
                {addToCartErrorMessage && (
                  <div className="draft-order-page__message draft-order-page__message--error">
                    {addToCartErrorMessage}
                  </div>
                )}

                </div>
                <div className="draft-order-card-row">
                  <h3 className="draft-order-page__subtitle">Customer</h3>
                  <p>{draftOrder.email}</p>
                </div>
             
                  <div className="draft-order-card-row">
                    <h3 className="draft-order-page__subtitle">Additional Information</h3>
                      {draftOrder.metafield && (
                        <p>
                          {draftOrder.metafield.definition.name}:{' '}
                          {draftOrder.metafield.value}
                        </p>
                      )}
                    {draftOrder.poNumber && <p>PO Number: {draftOrder.poNumber}</p>}
                  </div>
                
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default DraftOrderPage;
